import { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Beer, Minus, Pencil, Plus, Trash2, Upload, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { db, storage } from '../../firebase/config';
import { useTournament } from '../../contexts/TournamentContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import { cn } from '../../lib/utils';

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const EMPTY_FORM = { name: '', color: '#2391d6' };

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const TeamManagement = ({ tournamentId: propId = null }) => {
  const { selected } = useTournament();
  const tournamentId = propId || selected?.id || null;
  const currentTournament = selected;
  const isCumulative = currentTournament?.format === 'team-cumulative';
  const drinksEnabled = !!currentTournament?.enableDrinkCounter;

  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]); // tournament players subcollection
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [teamImage, setTeamImage] = useState(null);
  const [selectedParticipants, setSelectedParticipants] = useState([]);

  const [teamToDelete, setTeamToDelete] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTeam, setAssignTeam] = useState(null);
  const [assignPlayerId, setAssignPlayerId] = useState('');

  const loadAll = async () => {
    if (!tournamentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [teamsSnap, playersSnap] = await Promise.all([
        getDocs(collection(db, 'tournaments', tournamentId, 'teams')),
        getDocs(collection(db, 'tournaments', tournamentId, 'players')),
      ]);
      setTeams(
        teamsSnap.docs.map((d) => ({ id: d.id, ...d.data(), members: d.data().members || [] }))
      );
      setPlayers(playersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error('Could not load data.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const openTeamDialog = (team = null) => {
    setEditingTeam(team);
    if (team) {
      setForm({ name: team.name || '', color: team.color || '#2391d6' });
      if (isCumulative) {
        setSelectedParticipants((team.members || []).map((m) => m.userId));
      }
    } else {
      setForm(EMPTY_FORM);
      setSelectedParticipants([]);
    }
    setTeamImage(null);
    setTeamDialogOpen(true);
  };

  const toggleParticipant = (userId) =>
    setSelectedParticipants((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );

  const handleSubmitTeam = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !tournamentId) {
      toast.error('Team name is required.');
      return;
    }
    setSaving(true);
    try {
      let imageUrl = editingTeam?.imageUrl || '';
      if (teamImage) {
        const sref = ref(
          storage,
          `tournaments/${tournamentId}/teams/${teamImage.name}-${Date.now()}`
        );
        await uploadBytes(sref, teamImage);
        imageUrl = await getDownloadURL(sref);
      }

      let members = editingTeam?.members || [];
      if (isCumulative) {
        members = selectedParticipants.map((userId) => {
          const participant = players.find((p) => p.userId === userId);
          return {
            userId,
            username: participant?.name || 'Unknown',
            assignedMode: currentTournament?.mode || participant?.mode || '',
            drinkCount: participant?.drinkCount || 0,
            pointsContributed: participant?.totalPoints || 0,
          };
        });
      }

      const payload = {
        name: form.name.trim(),
        color: form.color,
        imageUrl,
        members,
        totalPoints: editingTeam?.totalPoints || 0,
        updatedAt: new Date(),
      };

      let teamId = editingTeam?.id;
      if (editingTeam) {
        await updateDoc(doc(db, 'tournaments', tournamentId, 'teams', editingTeam.id), payload);
        toast.success('Team updated.');
      } else {
        const docRef = await addDoc(collection(db, 'tournaments', tournamentId, 'teams'), {
          ...payload,
          createdAt: new Date(),
        });
        teamId = docRef.id;
        toast.success('Team created.');
      }

      // Team-cumulative: sync teamId onto player docs
      if (isCumulative) {
        const previousMemberIds = editingTeam?.members?.map((m) => m.userId) || [];
        const removed = previousMemberIds.filter((id) => !selectedParticipants.includes(id));
        for (const userId of removed) {
          const p = players.find((pl) => pl.userId === userId);
          if (p)
            await updateDoc(doc(db, 'tournaments', tournamentId, 'players', p.id), {
              teamId: null,
            });
        }
        for (const userId of selectedParticipants) {
          const p = players.find((pl) => pl.userId === userId);
          if (p)
            await updateDoc(doc(db, 'tournaments', tournamentId, 'players', p.id), {
              teamId,
            });
        }
      }

      setTeamDialogOpen(false);
      await loadAll();
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteTeam = async () => {
    if (!teamToDelete || !tournamentId) return;
    setSaving(true);
    try {
      if (isCumulative && teamToDelete.members) {
        for (const m of teamToDelete.members) {
          const p = players.find((pl) => pl.userId === m.userId);
          if (p)
            await updateDoc(doc(db, 'tournaments', tournamentId, 'players', p.id), {
              teamId: null,
            });
        }
      }
      await deleteDoc(doc(db, 'tournaments', tournamentId, 'teams', teamToDelete.id));
      if (teamToDelete.imageUrl) {
        try {
          await deleteObject(ref(storage, teamToDelete.imageUrl));
        } catch {
          /* image may already be gone */
        }
      }
      toast.success('Team deleted.');
      setTeamToDelete(null);
      await loadAll();
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const openAssignDialog = (team) => {
    setAssignTeam(team);
    setAssignPlayerId('');
    setAssignOpen(true);
  };

  const playerIdToTeam = useMemo(() => {
    const map = new Map();
    teams.forEach((t) => (t.members || []).forEach((m) => map.set(m.id || m.userId, t.id)));
    return map;
  }, [teams]);

  const assignablePlayers = useMemo(() => {
    if (!assignTeam) return [];
    const currentMemberIds = new Set((assignTeam.members || []).map((m) => m.id || m.userId));
    return players.filter((p) => !currentMemberIds.has(p.id));
  }, [players, assignTeam]);

  const handleAssignPlayer = async () => {
    if (!assignTeam || !assignPlayerId || !tournamentId) return;
    setSaving(true);
    try {
      const playerSnap = await getDoc(
        doc(db, 'tournaments', tournamentId, 'players', assignPlayerId)
      );
      if (!playerSnap.exists()) {
        toast.error('Player not found.');
        return;
      }
      const p = playerSnap.data();
      const memberPayload = {
        id: assignPlayerId,
        name: p.name,
        country: p.country,
        countryName: p.countryName,
        mode: p.mode,
        rank: p.rank,
        imageUrl: p.imageUrl || '',
      };

      // Remove from any existing team
      for (const t of teams) {
        const idx = (t.members || []).findIndex((m) => m.id === assignPlayerId);
        if (idx >= 0) {
          const next = [...t.members];
          next.splice(idx, 1);
          await updateDoc(doc(db, 'tournaments', tournamentId, 'teams', t.id), {
            members: next,
          });
        }
      }
      const nextMembers = [...(assignTeam.members || []), memberPayload];
      await updateDoc(doc(db, 'tournaments', tournamentId, 'teams', assignTeam.id), {
        members: nextMembers,
      });
      toast.success(`${p.name} added to ${assignTeam.name}.`);
      setAssignOpen(false);
      await loadAll();
    } catch (err) {
      toast.error('Assign failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const removePlayerFromTeam = async (team, member) => {
    try {
      const key = isCumulative ? 'userId' : 'id';
      const matchVal = isCumulative ? member.userId : member.id;
      const next = (team.members || []).filter((m) => m[key] !== matchVal);
      await updateDoc(doc(db, 'tournaments', tournamentId, 'teams', team.id), { members: next });

      if (isCumulative) {
        const p = players.find((pl) => pl.userId === member.userId);
        if (p)
          await updateDoc(doc(db, 'tournaments', tournamentId, 'players', p.id), {
            teamId: null,
          });
      }
      toast.success('Player removed.');
      await loadAll();
    } catch (err) {
      toast.error('Remove failed.', { description: err.message });
    }
  };

  const adjustDrinkCount = async (team, member, delta) => {
    const newCount = Math.max(0, (member.drinkCount || 0) + delta);
    try {
      const nextMembers = team.members.map((m) =>
        m.userId === member.userId ? { ...m, drinkCount: newCount } : m
      );
      await updateDoc(doc(db, 'tournaments', tournamentId, 'teams', team.id), {
        members: nextMembers,
      });
      const p = players.find((pl) => pl.userId === member.userId);
      if (p)
        await updateDoc(doc(db, 'tournaments', tournamentId, 'players', p.id), {
          drinkCount: newCount,
        });
      await loadAll();
    } catch (err) {
      toast.error('Update failed.', { description: err.message });
    }
  };

  if (!tournamentId) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-16 text-center text-sm text-muted-foreground">
        Select a tournament in the top bar to manage its teams.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={() => openTeamDialog()}>
          <Plus className="h-4 w-4" />
          New team
        </Button>
      </div>

      {isCumulative && (
        <div className="mb-8 grid gap-3 sm:grid-cols-3">
          <StatTile label="Mode" value={currentTournament?.mode || '—'} />
          <StatTile label="Participants" value={players.length} />
          <StatTile label="Drink counter" value={drinksEnabled ? 'Enabled' : 'Disabled'} />
        </div>
      )}

      {isCumulative && players.length === 0 && (
        <div className="mb-8 border border-dashed border-border/60 px-6 py-10 text-center text-sm text-muted-foreground">
          No participants assigned yet. Use the Players page to add users to this tournament first.
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="h-48 animate-pulse rounded-md border border-border/60 bg-muted/10"
            />
          ))}
        </div>
      ) : teams.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
          No teams yet. Create one above.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {teams.map((team) => (
            <Card key={team.id} className="border-border/60 bg-background/40">
              <CardHeader className="flex flex-row items-center justify-between gap-3 border-b border-border/60">
                <div className="flex items-center gap-3">
                  {team.imageUrl ? (
                    <div className="h-10 w-10 overflow-hidden rounded-md border border-border/60 bg-muted/20">
                      <img src={team.imageUrl} alt="" className="h-full w-full object-contain" />
                    </div>
                  ) : (
                    <span
                      className="h-8 w-8 rounded-md border border-border/60"
                      style={{ backgroundColor: team.color }}
                    />
                  )}
                  <div>
                    <CardTitle className="font-display text-lg">{team.name}</CardTitle>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      {(team.members || []).length} member
                      {(team.members || []).length === 1 ? '' : 's'}
                      {team.totalPoints > 0 && ` · ${team.totalPoints} pts`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!isCumulative && (
                    <Button variant="ghost" size="sm" onClick={() => openAssignDialog(team)}>
                      <UserPlus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openTeamDialog(team)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setTeamToDelete(team)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {(team.members || []).length === 0 ? (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    No members yet.
                  </div>
                ) : (
                  <ul className="divide-y divide-border/60">
                    {team.members.map((m) => (
                      <li key={m.userId || m.id} className="flex items-center gap-3 py-2">
                        <Avatar className="h-8 w-8">
                          {m.imageUrl && <AvatarImage src={m.imageUrl} alt="" />}
                          <AvatarFallback>{initials(m.name || m.username)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{m.name || m.username}</div>
                          {(m.countryName || m.assignedMode) && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                              {m.countryName}
                              {m.countryName && m.assignedMode ? ' · ' : ''}
                              {m.assignedMode}
                            </div>
                          )}
                        </div>
                        {isCumulative && drinksEnabled && (
                          <div className="hidden items-center gap-1 sm:flex">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => adjustDrinkCount(team, m, -1)}
                              disabled={!m.drinkCount}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </Button>
                            <Badge
                              variant="outline"
                              className="min-w-[2.5rem] justify-center font-mono tabular-nums"
                            >
                              <Beer className="h-3 w-3" />
                              {m.drinkCount || 0}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => adjustDrinkCount(team, m, +1)}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => removePlayerFromTeam(team, m)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Team dialog */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingTeam ? 'Edit team' : 'New team'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitTeam} className="grid gap-5">
            <Field label="Name" required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
              />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Color">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-10 w-14 cursor-pointer rounded-md border border-border/60 bg-transparent"
                  />
                  <Input
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                  />
                </div>
              </Field>
              <Field label="Logo">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setTeamImage(e.target.files?.[0] || null)}
                  className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
                />
                {(teamImage || editingTeam?.imageUrl) && (
                  <img
                    src={teamImage ? URL.createObjectURL(teamImage) : editingTeam.imageUrl}
                    alt=""
                    className="mt-2 h-16 w-16 rounded-md border border-border/60 bg-muted/20 object-contain"
                  />
                )}
              </Field>
            </div>

            {isCumulative && (
              <Field
                label="Team members"
                hint={
                  players.length === 0
                    ? 'No tournament participants yet. Add players first.'
                    : 'Tick to include. Each participant can only be on one team.'
                }
              >
                <div className="max-h-64 overflow-y-auto rounded-md border border-border/60">
                  {players.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                      No participants to choose from.
                    </div>
                  ) : (
                    <ul className="divide-y divide-border/60">
                      {players.map((p) => {
                        const userId = p.userId;
                        if (!userId) return null;
                        const checked = selectedParticipants.includes(userId);
                        const otherTeam = teams.find(
                          (t) =>
                            t.id !== editingTeam?.id &&
                            (t.members || []).some((m) => m.userId === userId)
                        );
                        return (
                          <li key={p.id}>
                            <label
                              className={cn(
                                'flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-secondary/50',
                                otherTeam && !checked && 'opacity-60'
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleParticipant(userId)}
                                className="h-4 w-4"
                              />
                              <Avatar className="h-7 w-7">
                                {p.imageUrl && <AvatarImage src={p.imageUrl} alt="" />}
                                <AvatarFallback>{initials(p.name)}</AvatarFallback>
                              </Avatar>
                              <span className="flex-1 truncate">{p.name}</span>
                              {otherTeam && !checked && (
                                <Badge variant="outline">on {otherTeam.name}</Badge>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </Field>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setTeamDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editingTeam ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign player dialog (non-cumulative formats) */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign player to {assignTeam?.name}</DialogTitle>
          </DialogHeader>
          <Field
            label="Player"
            hint={assignablePlayers.length === 0 ? 'No available players.' : undefined}
          >
            <NativeSelect
              value={assignPlayerId}
              onChange={(e) => setAssignPlayerId(e.target.value)}
            >
              <option value="">— Select player —</option>
              {assignablePlayers.map((p) => {
                const existingTeamId = playerIdToTeam.get(p.id);
                const existingTeam = teams.find((t) => t.id === existingTeamId);
                return (
                  <option key={p.id} value={p.id}>
                    {p.name} {existingTeam ? `(on ${existingTeam.name})` : ''}
                  </option>
                );
              })}
            </NativeSelect>
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignPlayer} disabled={saving || !assignPlayerId}>
              <Upload className="h-4 w-4" />
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete team confirm */}
      <Dialog open={!!teamToDelete} onOpenChange={(o) => !o && setTeamToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete team?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Removes <span className="font-medium text-foreground">{teamToDelete?.name}</span>.{' '}
            {isCumulative
              ? 'Its members return to the unassigned pool.'
              : 'Member assignments on this team are wiped.'}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTeamToDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTeam} disabled={saving}>
              Delete team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatTile = ({ label, value }) => (
  <div className="rounded-md border border-border/60 bg-background/40 px-4 py-3">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
      {label}
    </div>
    <div className="mt-1 font-display text-2xl font-bold">{value}</div>
  </div>
);

export default TeamManagement;
